import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getZoomLevel,
  createZoomChangeDetector,
  getZoomCorrectedRect,
  getZoomCorrectedEventCoords,
} from '../utils/zoom';

/**
 * Hook for zoom-aware functionality
 * Provides current zoom level and zoom-corrected utilities
 *
 * @returns {Object} Zoom utilities and state
 */
export default function useZoom() {
  const [zoomLevel, setZoomLevel] = useState(() => getZoomLevel());
  const zoomChangeCallbacksRef = useRef([]);

  // Update zoom level periodically
  useEffect(() => {
    const cleanup = createZoomChangeDetector((newZoom) => {
      setZoomLevel(newZoom);

      // Notify any registered callbacks
      zoomChangeCallbacksRef.current.forEach(callback => {
        try {
          callback(newZoom);
        } catch (error) {
          console.error('Error in zoom change callback:', error);
        }
      });
    });

    return cleanup;
  }, []);

  // Register a callback for zoom changes
  const onZoomChange = useCallback((callback) => {
    zoomChangeCallbacksRef.current.push(callback);

    // Return cleanup function
    return () => {
      const index = zoomChangeCallbacksRef.current.indexOf(callback);
      if (index > -1) {
        zoomChangeCallbacksRef.current.splice(index, 1);
      }
    };
  }, []);

  // Get zoom-corrected rectangle for a DOM element
  const getCorrectedRect = useCallback((element) => {
    if (!element || !element.getBoundingClientRect) {
      console.warn('useZoom: Invalid element provided to getCorrectedRect');
      return null;
    }

    const rect = element.getBoundingClientRect();
    return getZoomCorrectedRect(rect);
  }, []);

  // Get zoom-corrected event coordinates
  const getCorrectedEventCoords = useCallback((event) => {
    return getZoomCorrectedEventCoords(event);
  }, []);

  return {
    zoomLevel,
    getCorrectedRect,
    getCorrectedEventCoords,
    onZoomChange,
  };
}
